// Kiri — Fieldlink's in-app AI action assistant (character experience).
// ASK -> answer. CREATE -> draft (site diary / job / task). DO -> preview +
// explicit confirm before anything is written. Never bypasses role/org scope
// — all permission/action logic lives server-side in /app/backend/kiri.py.
// This file is purely the experience layer: character, animation,
// conversation and card UI wrapped around that unchanged backend contract.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Send, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { KiriCharacter } from "./kiri/KiriCharacter";
import { KiriHome } from "./kiri/KiriHome";
import {
  DeniedCard, NavigateCard, FindCard, SummaryCard, JobDraftCard,
  TaskCreatedCard, DiaryCreatedCard, PendingCard, CancelledNote, ErrorCard, FollowUpChips,
  InsightsCard, CreatedCard,
} from "./kiri/KiriActionCards";

function newSessionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function jobIdFromPath(pathname) {
  const m = pathname.match(/^\/app\/jobs\/([a-zA-Z0-9-]+)(?:\/|$)/);
  return m && m[1] !== "new" ? m[1] : null;
}

function followUpsFor(result) {
  if (!result) return [];
  if (result.type === "summary") return ["Write today's site diary", "Add a task"];
  return [];
}

const THINKING_LINES = ["Kiri is thinking…", "Checking your Fieldlink data…"];

export function Kiri() {
  const { user } = useAuth();
  return <KiriSession key={`${user?.org_id || "none"}:${user?.id || "none"}:${user?.role || "none"}`} />;
}

function KiriSession() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [execBusy, setExecBusy] = useState(false);
  const [thinkingLine, setThinkingLine] = useState(0);
  const [transient, setTransient] = useState(null);
  const [jobTitle, setJobTitle] = useState(null);
  const [recent, setRecent] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const transientTimer = useRef(null);
  const draggedRef = useRef(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(`fl_kiri_hidden_${user?.id || "x"}`) === "1"; } catch { return false; }
  });
  const [pos, setPos] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem("fl_kiri_pos") || "null");
      return p && typeof p.x === "number" && typeof p.y === "number" ? p : null;
    } catch { return null; }
  });

  const jobId = useMemo(() => jobIdFromPath(loc.pathname), [loc.pathname]);
  const busy = chatBusy || execBusy;
  const [sessionId, setSessionId] = useState(() => {
    const key = `fl_kiri_session_${user?.org_id || "none"}_${user?.id || "x"}_${user?.role || "none"}`;
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = newSessionId();
    localStorage.setItem(key, fresh);
    return fresh;
  });

  const flash = (state, ms = 1400) => {
    clearTimeout(transientTimer.current);
    setTransient(state);
    transientTimer.current = setTimeout(() => setTransient(null), ms);
  };

  const characterState = chatBusy ? "thinking" : execBusy ? "creating" : transient || (inputFocused ? "listening" : "idle");

  useEffect(() => {
    api.get("/kiri/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!jobId) { setJobTitle(null); return; }
    api.get(`/jobs/${jobId}`).then((r) => setJobTitle(r.data?.title || null)).catch(() => setJobTitle(null));
  }, [jobId]);

  useEffect(() => {
    if (!open) return;
    api.get("/kiri/history", { params: { session_id: sessionId } })
      .then((r) => setMessages((r.data.items || []).map((m) => ({ ...m }))))
      .catch(() => {});
    api.get("/kiri/recent").then((r) => setRecent(r.data.items || [])).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 280);
  }, [open, sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, chatBusy]);

  useEffect(() => {
    if (!chatBusy) return;
    const t = setInterval(() => setThinkingLine((i) => (i + 1) % THINKING_LINES.length), 1400);
    return () => clearInterval(t);
  }, [chatBusy]);

  // Other pages (e.g. Financials' Kiri brief) can ask Kiri a question directly
  useEffect(() => {
    const onAsk = (e) => {
      const q = e.detail?.question;
      if (!q) return;
      setOpen(true);
      setTimeout(() => send(q), 320);
    };
    window.addEventListener("fl:kiri-ask", onAsk);
    return () => window.removeEventListener("fl:kiri-ask", onAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closePanel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openPanel = () => {
    setLifting(true);
    setTimeout(() => { setOpen(true); setLifting(false); }, 160);
  };
  const closePanel = () => setOpen(false);

  // Per-user hide (dismiss the floating launcher). Restored from the profile menu.
  const hideKiri = (e) => {
    e?.stopPropagation?.();
    setHidden(true);
    try { localStorage.setItem(`fl_kiri_hidden_${user?.id || "x"}`, "1"); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("fl:kiri-hidden"));
    toast("Kiri hidden — turn it back on from your profile menu", { duration: 4000, position: "bottom-center" });
  };

  useEffect(() => {
    const onShow = () => {
      setHidden(false);
      try { localStorage.setItem(`fl_kiri_hidden_${user?.id || "x"}`, "0"); } catch { /* ignore */ }
    };
    window.addEventListener("fl:kiri-show", onShow);
    return () => window.removeEventListener("fl:kiri-show", onShow);
  }, [user?.id]);

  // Draggable launcher — remembers position per device. A tap that doesn't move
  // opens Kiri; a drag repositions it (and suppresses the click-to-open).
  const onLauncherPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const rect = launcherRef.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;
    const start = { sx: e.clientX, sy: e.clientY, ox: rect.left, oy: rect.top, w: rect.width, h: rect.height };
    let moved = false;
    let last = null;
    const move = (ev) => {
      const dx = ev.clientX - start.sx;
      const dy = ev.clientY - start.sy;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
      if (moved) {
        const nx = Math.max(8, Math.min(window.innerWidth - start.w - 8, start.ox + dx));
        const ny = Math.max(8, Math.min(window.innerHeight - start.h - 8, start.oy + dy));
        last = { x: nx, y: ny };
        setPos(last);
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      draggedRef.current = moved;
      if (moved && last) {
        try { localStorage.setItem("fl_kiri_pos", JSON.stringify(last)); } catch { /* ignore */ }
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onLauncherClick = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    openPanel();
  };

  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) {
      setTimeout(() => launcherRef.current?.focus(), 0);
    }
    wasOpen.current = open;
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || chatBusy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setChatBusy(true);
    try {
      const { data } = await api.post("/kiri/chat", {
        session_id: sessionId, message: msg,
        context: { page: loc.pathname, job_id: jobId, section: new URLSearchParams(loc.search).get("section") || new URLSearchParams(loc.search).get("tab") },
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, result: data.result, pending_action: data.pending_action, intent: data.intent, mode: data.mode, source_labels: data.source_labels, suggested_actions: data.suggested_actions }]);
      if (data.result && !data.result.denied) flash("success", 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kiri couldn't answer just now");
      setMessages((m) => [...m, { role: "assistant", error: true, failedMessage: msg }]);
      flash("error");
    } finally {
      setChatBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const cancelPending = (idx) => {
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, pending_action: null, cancelled: true } : x)));
  };

  const confirmPending = async (idx, action) => {
    setExecBusy(true);
    try {
      const { data } = await api.post("/kiri/actions/execute", {
        session_id: sessionId, action_id: action.id, params: action.params, confirmation_id: action.confirmation_id,
      });
      setMessages((m) => [
        ...m.map((x, i) => (i === idx ? { ...x, pending_action: null } : x)),
        { role: "assistant", content: data.message, result: data.result },
      ]);
      flash("success", 1600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That did not finish. Check the record before trying again.");
      flash("error");
    } finally {
      setExecBusy(false);
    }
  };

  const goTo = async (path) => {
    try {
      const { data } = await api.post("/kiri/navigation", { target: path });
      if (typeof data.target === "string" && data.target.startsWith("/app") && !/[\\\r\n]/.test(data.target)) { nav(data.target); closePanel(); }
    } catch { toast.error("That page isn't available with your current access."); }
  };
  const prefill = (text) => { setInput(text); setTimeout(() => inputRef.current?.focus(), 50); };
  const newChat = () => {
    const fresh = newSessionId();
    localStorage.setItem(`fl_kiri_session_${user?.org_id || "none"}_${user?.id || "x"}_${user?.role || "none"}`, fresh);
    setSessionId(fresh);
    setMessages([]);
  };

  const renderResultBody = (result) => {
    if (!result) return null;
    if (result.denied) return <DeniedCard text={result.denied} />;
    if (result.type === "tool_read" && result.data) {
      const { title, summary, details, path, references, sections } = result.data;
      return <div className="rounded-lg border border-white/10 p-3 space-y-2 text-sm text-zinc-200">
        <p className="font-semibold">{title}</p><p>{summary}</p>
        {details?.items?.map((item, index) => <div key={item.id || index} className="border-t border-white/10 pt-2">
          {Object.entries(item).filter(([key, value]) => !key.endsWith("_id") && key !== "id" && key !== "source_timesheet" && value != null && value !== "").map(([key, value]) => <p key={key} className="text-xs"><span className="text-zinc-400">{key.replaceAll("_", " ")}: </span>{String(value)}</p>)}
        </div>)}
        {sections?.filter(section => section.items?.length).map((section, sectionIndex) => <details key={sectionIndex} className="border-t border-white/10 pt-2">
          <summary className="cursor-pointer text-xs font-semibold">{section.title}</summary>
          {section.items.map((item, index) => <div key={index} className="mt-2 text-xs">
            {Object.entries(item).filter(([key, value]) => !key.endsWith("_id") && key !== "id" && value != null).map(([key, value]) => <p key={key}><span className="text-zinc-400">{key.replaceAll("_", " ")}: </span>{String(value)}</p>)}
          </div>)}
        </details>)}
        {references?.length > 0 && <div className="flex flex-wrap gap-2">{references.map((reference, index) => <Button key={index} variant="outline" size="sm" onClick={() => goTo(reference.target)}>{reference.label}</Button>)}</div>}
        {path && <Button variant="outline" size="sm" onClick={() => goTo(path)}>Open in Fieldlink</Button>}
      </div>;
    }
    if (result.type === "navigate" && result.data) return <NavigateCard data={result.data} onGo={() => goTo(result.data.path)} />;
    if ((result.type === "find_jobs" || result.type === "find_clients") && result.data) {
      const isJob = result.type === "find_jobs";
      return (
        <FindCard
          isJob={isJob}
          items={result.data.items || []}
          onGo={(it) => goTo(isJob ? `/app/jobs/${it.id}` : "/app/clients")}
        />
      );
    }
    if (result.type === "summary" && result.data) return <SummaryCard data={result.data} onOpen={() => goTo(`/app/jobs/${result.data.job_id}`)} />;
    if (result.type === "job_draft" && result.data) {
      const qs = new URLSearchParams(Object.entries(result.data).filter(([, v]) => v)).toString();
      return <JobDraftCard data={result.data} onOpen={() => goTo(`/app/jobs/new?${qs}`)} />;
    }
    if (result.type === "task_created" && result.data) return <TaskCreatedCard data={result.data} />;
    if (result.type === "diary_created" && result.data) return <DiaryCreatedCard data={result.data} onOpen={() => goTo(`/app/jobs/${result.data.job_id}`)} />;
    if (result.type === "insights") return <InsightsCard data={result.data} onOpenJob={(id) => goTo(`/app/jobs/${id}`)} />;
    if (result.type === "variation_created" && result.data) return <CreatedCard label="VARIATION PREPARED" message={`${result.data.variation_number} on ${result.data.job_title}`} onOpen={() => goTo(`/app/jobs/${result.data.job_id}`)} openLabel="OPEN JOB" testId="kiri-variation-created" />;
    if (result.type === "toolbox_created" && result.data) return <CreatedCard label="TOOLBOX TALK STARTED" message={result.data.title} onOpen={() => goTo(`/app/toolbox/${result.data.id}`)} openLabel="OPEN TALK" testId="kiri-toolbox-created" />;
    if (result.type === "client_created" && result.data) return <CreatedCard label="CLIENT ADDED" message={result.data.name} onOpen={() => goTo("/app/clients")} openLabel="VIEW CLIENTS" testId="kiri-client-created" />;
    if (result.type === "job_created" && result.data) return <CreatedCard label="JOB CREATED" message={`${result.data.job_number || ""} ${result.data.title}`.trim()} onOpen={() => goTo(`/app/jobs/${result.data.id}`)} openLabel="OPEN JOB" testId="kiri-job-created" />;
    return null;
  };

  if (status && status.enabled === false) return null;
  if (hidden) return null;
  if (loc.pathname.startsWith("/app/onboarding")) return null; // Kiri IS the page there
  if (/^\/app\/quotes\/[^/]+\/items$/.test(loc.pathname)) return null; // full-screen line item grid — no overlap
  if (typeof window !== "undefined" && window.self !== window.top) return null; // inside the onboarding live-preview iframe — narration already handled outside it

  return (
    <>
      {!open && (
        <div className={`hidden md:flex fixed z-[70] items-center gap-1 rounded-full border border-zinc-200 dark:border-slate-600 bg-white dark:bg-slate-900 p-1 shadow-md kiri-launcher-pos ${lifting ? "kiri-launcher-lift" : ""}`}
          style={pos ? { left: `clamp(8px, ${pos.x}px, calc(100vw - 200px))`, top: `clamp(8px, ${pos.y}px, calc(100dvh - 108px))`, right: "auto", bottom: "auto" } : undefined}>
          <button ref={launcherRef} type="button" onPointerDown={onLauncherPointerDown} onClick={onLauncherClick}
            className="flex min-h-20 items-center gap-1 rounded-full pl-1 pr-3 text-sm font-semibold text-zinc-800 dark:text-white hover:bg-zinc-100 dark:hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 cursor-grab touch-none"
            aria-label="Open Kiri Assistant (drag to move)" title="Ask Kiri" data-testid="kiri-launcher">
            <KiriCharacter state={characterState} size="lg" useHero /><span>Kiri</span>
          </button>
          <button type="button" onClick={hideKiri} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 dark:hover:bg-slate-800" aria-label="Hide Kiri" title="Hide Kiri" data-testid="kiri-hide"><X size={14}/></button>
        </div>
      )}

      {open && (
        <div
          className="hidden md:flex sm:inset-auto sm:bottom-5 sm:right-5 fixed z-[80] sm:w-[min(440px,calc(100vw-2rem))] sm:h-[min(700px,calc(100dvh-6rem))] bg-[#0B0F14] sm:rounded-2xl flex-col overflow-hidden kiri-panel-enter shadow-[0_0_0_1px_rgba(255,95,21,0.16),0_25px_70px_-15px_rgba(0,0,0,0.65)]"
          data-testid="kiri-panel"
          role="dialog"
          aria-label="Kiri Assistant"
        >
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/5 shrink-0">
            <KiriCharacter state={characterState} size="md" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-base leading-none text-white">Kiri</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Fieldlink Assistant</div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-zinc-500 mr-1">
              <span className={`w-1.5 h-1.5 rounded-full ${busy ? "bg-[#FF5F15] animate-pulse" : "bg-emerald-500"}`} />
              {busy ? "Thinking" : "Ready to help"}
            </div>
            <button type="button" onClick={newChat} className="text-zinc-500 hover:text-white transition-colors p-1" title="New chat" aria-label="Start a new Kiri chat" data-testid="kiri-new-chat">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button type="button" onClick={closePanel} className="text-zinc-500 hover:text-white transition-colors p-1" aria-label="Close Kiri Assistant" data-testid="kiri-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3.5 py-3" data-testid="kiri-messages">
            {messages.length === 0 && !chatBusy && (
              <KiriHome user={user} pathname={loc.pathname} jobTitle={jobTitle} onSend={send} onPrefill={prefill} recent={recent} />
            )}

            {messages.map((m, i) => {
              if (m.role === "user") {
                return (
                  <div key={i} className="flex justify-end mb-3">
                    <div className="max-w-[85%] bg-[#231710] border border-[#3A2418] text-white px-3.5 py-2.5 text-sm rounded-2xl rounded-br-md" data-testid="kiri-msg-user">
                      {m.content}
                    </div>
                  </div>
                );
              }
              if (m.error) {
                return (
                  <div key={i} className="flex items-start gap-2 mb-3">
                    <KiriCharacter state="error" size="sm" />
                    <div className="max-w-[85%] min-w-0">
                      <ErrorCard onRetry={() => send(m.failedMessage)} />
                    </div>
                  </div>
                );
              }
              const chips = followUpsFor(m.result);
              return (
                <div key={i} className="flex items-start gap-2 mb-3">
                  <KiriCharacter state="idle" size="sm" />
                  <div className="max-w-[88%] min-w-0 flex-1">
                    <div className="bg-[#12161C] text-zinc-100 px-3.5 py-2.5 text-sm rounded-2xl rounded-bl-md" data-testid="kiri-msg-assistant">
                      {m.content}
                    </div>
                    {m.source_labels?.length > 0 && <p className="mt-1 text-[11px] text-zinc-400">{m.mode} · {m.source_labels.join(" · ")}</p>}
                    {m.suggested_actions?.filter(action => action.type === "prompt").length > 0 && <div className="mt-2 flex flex-wrap gap-2">
                      {m.suggested_actions.filter(action => action.type === "prompt").map((action, index) => <Button key={index} size="sm" variant="outline" disabled={chatBusy} onClick={() => send(action.prompt)}>{action.label}</Button>)}
                    </div>}
                    {renderResultBody(m.result)}
                    {m.pending_action && (
                      <PendingCard
                        action={m.pending_action}
                        busy={execBusy}
                        onCancel={() => cancelPending(i)}
                        onConfirm={() => confirmPending(i, m.pending_action)}
                      />
                    )}
                    {m.cancelled && !m.pending_action && <CancelledNote />}
                    <FollowUpChips chips={chips} onPick={send} />
                  </div>
                </div>
              );
            })}

            {chatBusy && (
              <div className="flex items-center gap-2 mb-2" data-testid="kiri-typing">
                <KiriCharacter state="thinking" size="sm" />
                <span className="text-xs text-zinc-500">{thinkingLine === 0 ? "Checking your context and available capabilities…" : THINKING_LINES[thinkingLine]}</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 px-3 py-3 border-t border-white/5 shrink-0">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder="Ask Kiri to create, find, summarise or explain…"
              maxLength={1000}
              className="flex-1 min-w-0 h-11 bg-[#161B22] border border-white/5 rounded-full px-4 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF5F15]/50"
              data-testid="kiri-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="h-11 w-11 shrink-0 rounded-full bg-[#FF5F15] hover:bg-[#E65100] disabled:opacity-40 disabled:hover:bg-[#FF5F15] text-white flex items-center justify-center transition-colors"
              data-testid="kiri-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
